import { useEffect, useCallback, useState, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { subscribeStreamingEvents } from '../streaming/streamingSubscription';
import { isAccountInactiveCode, signalAccountInactive } from '../services/accountInactiveSignal';

// En desarrollo usamos el proxy local, en producción la URL de WebSocket
const SOCKET_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_WS_URL
  : (process.env.REACT_APP_API_URL || 'http://localhost:5678');

// Eliminamos DEMO_CHAT_ID ya que no lo usaremos para la conexión principal
// const DEMO_CHAT_ID = 'demo-chat-2024';

// ✅ FIX: Module-level map to track threads that have been connected at least once.
// Survives component unmount/remount (e.g. navigating away from Chat and back).
// This allows detecting "reconnection to same thread" after remount, triggering
// a message refetch to recover messages lost while the socket was disconnected.
// Resets only on full page reload (correct behavior — fresh page = fresh state).
const connectedThreads = {};

// Añadimos threadId como argumento principal
// onThreadTitleUpdated: Callback cuando se actualiza título (threadId, newTitle) => void
export const useWebSocket = (threadId, username = 'usuario', onThreadTitleUpdated = null) => { 
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // Keep this state
  const [error, setError] = useState(null);
  // const [currentQuestion, setCurrentQuestion] = useState(null); - removed, questions feature deprecated
  const [agents, setAgents] = useState({});
  const [agentStatus, setAgentStatus] = useState(null); // <-- NUEVO estado
  const [newlyCreatedThreadData, setNewlyCreatedThreadData] = useState(null); // <<< NUEVO ESTADO PARA HILOS DE N8N
  const [watchdogTimeout, setWatchdogTimeout] = useState(null); // ✅ Estado para watchdog timeout de N8N

  // ✅ CRITICAL: Ref to store Promise resolver for connection waiting
  // This allows useMessageSending to wait for WebSocket connection before sending
  const connectionResolverRef = useRef(null);
  const connectionPromiseRef = useRef(null);

  // ✅ PERFORMANCE: Ref to store current socket for stable subscribeToMessages
  // Without this, subscribeToMessages would recreate on every socket change
  const socketRef = useRef(null);

  // ✅ FIX: Track reconnection for message recovery
  const [hasReconnected, setHasReconnected] = useState(false);

  const { session, loading: loadingAuth } = useAuth();
  const authToken = session?.access_token;

  // Función para actualizar el estado del agente desde fuera (ej: API REST)
  const setAgentStatusManually = useCallback((status) => {
    console.log('[useWebSocket] Setting agent status manually:', status);
    // Ahora esperamos recibir directamente { agentName, currentTask, lastUpdated } o null
    if (status === null || (status && status.agentName !== undefined && status.currentTask !== undefined)) {
        // Si es null, se establece a null. Si tiene agentName/currentTask, se usa directamente.
        setAgentStatus(status);
    } else {
        // Si el formato no es el esperado (ej: viene con campos antiguos o solo una de las props)
        console.warn('[useWebSocket] Formato inesperado recibido en setAgentStatusManually. Esperando {agentName, currentTask} o null. Estableciendo a null.', status);
        setAgentStatus(null);
    }
  }, []);

  // Inicializar conexión - ahora depende de threadId y authToken (del contexto)
  useEffect(() => {
    if (!threadId) {
        console.log("useWebSocket: No threadId provided, skipping connection.");
        if(socket) {
            socket.disconnect();
            setSocket(null);
            setIsConnected(false);
        }
        setAgentStatus(null);
        return; 
    }

    if (loadingAuth) {
      console.warn("useWebSocket: Auth state is loading, WebSocket connection deferred.");
       if(socket) { socket.disconnect(); setSocket(null); setIsConnected(false); }
       return;
    }

    if (!authToken) {
      console.warn("useWebSocket: No authToken from context, WebSocket connection not established.");
      if(socket) { socket.disconnect(); setSocket(null); setIsConnected(false); }
      setAgentStatus(null); 
      return;
    }

    console.log(`useWebSocket: Connecting for threadId: ${threadId} with authToken: ${authToken ? authToken.substring(0, 6) + '...' : 'null'}`);

    // ✅ CRITICAL: Create Promise for waiting on connection
    // This allows useMessageSending to wait for WebSocket to be ready
    connectionPromiseRef.current = new Promise((resolve) => {
      connectionResolverRef.current = resolve;
    });

    const socketInstance = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      auth: {
        token: authToken
      },
      query: {
        chatId: threadId
      },
      // Explicitly enable transports for robustness
      transports: ['websocket', 'polling']
    });

    setSocket(socketInstance);
    socketRef.current = socketInstance; // ✅ Keep ref in sync
    setIsConnected(false); // Reset connection status until 'connect' event fires

    // Listener 'connect'
    const handleConnect = () => {
      console.log(`WebSocket connected for threadId: ${threadId}, socketId: ${socketInstance.id}`);

      // 🟢 WINDOWS DEBUG: Detailed connection info
      console.log('🟢 WEBSOCKET CONNECTED - Windows debug:', {
        socketId: socketInstance.id,
        threadId,
        timestamp: new Date().toISOString(),
        transport: socketInstance.io?.engine?.transport?.name || 'unknown',
        url: SOCKET_URL,
        platform: navigator.platform,
        userAgent: navigator.userAgent.substring(0, 50) + '...'
      });

      // ✅ FIX: Detect reconnection (network recovery, token refresh, OR remount after navigation)
      // connectedThreads is module-level so it survives component unmount/remount.
      // If we've connected to this thread before in this page session, treat as reconnection
      // → triggers message refetch to recover anything lost while socket was disconnected.
      const isReconnection = connectedThreads[threadId] === true;
      if (isReconnection) {
        console.log(`[useWebSocket] 🔄 RECONNECTION detected for thread ${threadId} (network recovery, token refresh, or remount)`);
        setHasReconnected(true);
      } else {
        console.log(`[useWebSocket] 🔵 FIRST connection for thread ${threadId}`);
      }
      connectedThreads[threadId] = true;

      setIsConnected(true);
      setError(null);

      // ✅ CRITICAL: Resolve connection promise when connected
      // This allows useMessageSending to proceed with sending message
      if (connectionResolverRef.current) {
        console.log('[useWebSocket] ✅ Resolving connection promise');
        connectionResolverRef.current();
        connectionResolverRef.current = null; // Clean up
      }
    };
    socketInstance.on('connect', handleConnect);

    // Listener 'disconnect'
    const handleDisconnect = (reason) => {
      console.log(`WebSocket disconnected for threadId: ${threadId}. Reason: ${reason}`);
      setIsConnected(false);
      // Considerar limpieza adicional si es necesario
    };
    socketInstance.on('disconnect', handleDisconnect);
    
    // Listener 'error'
    const handleError = (errorObj) => {
      // #559: PRIMERO de todo, antes de las supresiones de más abajo. Este handler está
      // suscrito a 'connect_error' y a 'error' (:216-217), y el handshake rechazado por cuenta
      // cortada llega con el código a secas en `message`. Con el early return se consigue
      // además que ese código NO caiga en el `setError` del final, que lo pintaría literal
      // ("ACCOUNT_SUSPENDED") en la UI del chat.
      if (isAccountInactiveCode(errorObj?.message)) {
        console.warn(`[useWebSocket] Cuenta no activa (${errorObj.message}) para threadId: ${threadId}`);
        signalAccountInactive({ code: errorObj.message, source: 'useWebSocket:error' });
        setIsConnected(false);
        return;
      }

      // 🔴 WINDOWS DEBUG: Log all WebSocket errors for diagnosis
      console.log('🔴 WEBSOCKET ERROR - Windows debug:', {
        errorMessage: errorObj?.message,
        errorType: errorObj?.type,
        errorDescription: errorObj?.description,
        threadId,
        timestamp: new Date().toISOString(),
        fullError: JSON.stringify(errorObj)
      });

      // Suprimir errores de conexión cuando el backend no está disponible
      const isConnectionError = errorObj && (
        errorObj.message?.includes('ERR_CONNECTION_REFUSED') ||
        errorObj.message?.includes('Failed to load') ||
        errorObj.message?.includes('NetworkError') ||
        errorObj.type === 'TransportError' ||
        errorObj.description === 'websocket error'
      );

      if (isConnectionError) {
        // Solo loguear en modo desarrollo, no mostrar al usuario
        console.warn(`[useWebSocket] ⚠️ Backend no disponible para threadId: ${threadId} (connection error suppressed)`);
        setIsConnected(false);
        return;
      }

      // Comprobar si es el error específico de N8N que no queremos que impacte la UI
      const n8nErrorMessage = "Error al procesar tu respuesta";
      const n8nErrorDetails = "Request failed with status code 404";

      if (errorObj && 
          ((typeof errorObj.message === 'string' && errorObj.message.includes(n8nErrorMessage)) || 
           (typeof errorObj.details === 'string' && errorObj.details.includes(n8nErrorDetails))) ) {
        // Es el error de N8N, lo logueamos en consola pero no actualizamos el estado de error de la UI
        console.warn("[useWebSocket] Error de comunicación con N8N detectado y suprimido para la UI.", errorObj);
      } else {
        // Para cualquier otro error, actualizar el estado para que se muestre en la UI
        setError(errorObj.message || 'Unknown WebSocket error');
      }
      // Considerar si un error (incluso el de N8N no mostrado) debe marcar como desconectado
      // Por ahora, mantenemos la lógica original de que cualquier error en 'error' o 'connect_error' puede ser una desconexión.
      // Si el socket sigue funcional para otros eventos, quizás no siempre se deba hacer setIsConnected(false).
      // Pero para este caso específico, si es un error de aplicación post-conexión, el socket en sí puede estar bien.
      // La línea original era: setIsConnected(false); // Considerar si un error debe marcar como desconectado
      // Si el error es solo de N8N, no necesariamente significa que el socket está desconectado.
      // Vamos a ser más selectivos con setIsConnected(false)
      if (! (errorObj && ((typeof errorObj.message === 'string' && errorObj.message.includes(n8nErrorMessage)) || (typeof errorObj.details === 'string' && errorObj.details.includes(n8nErrorDetails))))) {
        setIsConnected(false); // Marcar como desconectado solo para errores que no sean el de N8N filtrado
      }
    };
    socketInstance.on('connect_error', handleError); // También escuchar 'connect_error'
    socketInstance.on('error', handleError); // Listener genérico de error

    // Listener 'agent_typing'
    const handleAgentTyping = () => setIsTyping(true);
    socketInstance.on('agent_typing', handleAgentTyping);

    // Listener 'agent_stop_typing'
    const handleAgentStopTyping = () => setIsTyping(false);
    socketInstance.on('agent_stop_typing', handleAgentStopTyping);

    // Listener 'question'
    const handleQuestion = (data) => {
      console.log('Pregunta recibida:', {
        threadId: data?.threadId,
        planId: data?.planId,
        questionId: data?.questionId,
        data: data?.data ? '***[question data]***' : null
      });
      // Questions feature deprecated - handler removed
      // setCurrentQuestion(data);
    };
    // socketInstance.on('question', handleQuestion); - removed, questions feature deprecated

    // Listener 'agents_update'
    const handleAgentsUpdate = (agentsData) => {
      console.log('Actualización de agentes recibida:', {
        count: Object.keys(agentsData || {}).length,
        agents: Object.keys(agentsData || {})
      });
      setAgents(agentsData);
    };
    socketInstance.on('agents_update', handleAgentsUpdate);

    // --- NUEVO Listener para agent_status_updated ---
    const handleAgentStatusUpdate = (payload) => {
      // 🔔 WINDOWS DEBUG: Always log agent status updates
      console.log('🔔 AGENT_STATUS_UPDATED received - Windows debug:', {
        receivedThreadId: payload?.threadId,
        currentThreadId: threadId,
        matches: payload?.threadId === threadId,
        agentName: payload?.agentName,
        currentTask: payload?.currentTask ? payload.currentTask.substring(0, 50) + '...' : null,
        lastUpdated: payload?.lastUpdated,
        timestamp: new Date().toISOString()
      });

      // 🔍 DEBUG: Log tipo de datos recibidos vía WebSocket
      console.log('[useWebSocket] 🔍 Tipos de datos en payload WebSocket:', {
        agentName_raw: payload?.agentName,
        agentName_typeof: typeof payload?.agentName,
        agentName_is_null: payload?.agentName === null,
        agentName_is_undefined: payload?.agentName === undefined,
        currentTask_raw: payload?.currentTask,
        currentTask_typeof: typeof payload?.currentTask,
        currentTask_is_null: payload?.currentTask === null,
        currentTask_is_undefined: payload?.currentTask === undefined
      });

      // Verificar si la actualización es para el hilo actual conectado
      if (payload && payload.threadId === threadId) {
        console.log('[useWebSocket] ✅ Setting agentStatus:', {
          agentName: payload.agentName,
          currentTask: payload.currentTask,
          lastUpdated: payload.lastUpdated
        });
        setAgentStatus({
          agentName: payload.agentName,
          currentTask: payload.currentTask,
          lastUpdated: payload.lastUpdated,
          color: payload.color ?? null,
        });
      } else {
        console.log('[useWebSocket] ❌ Ignorando agent_status_updated para otro threadId:', payload?.threadId);
      }
    };
    socketInstance.on('agent_status_updated', handleAgentStatusUpdate);
    // --- Fin NUEVO Listener ---

    // #494: el evento de ACK de stop está RETIRADO — la confirmación real del
    // corte es stream-cancelled (lo consume useTurnLifecycle vía el consumer).

    // ✅ Listener para watchdog timeout (N8N dejó de comunicarse)
    const handleWatchdogTimeout = (payload) => {
      console.log('[useWebSocket] ⏱️ watchdog_timeout received:', payload);
      if (payload?.threadId === threadId) {
        console.log('[useWebSocket] ✅ Watchdog timeout for current thread, setting state');
        setWatchdogTimeout({ threadId: payload.threadId, timestamp: payload.timestamp });
      } else {
        console.log('[useWebSocket] ❌ Ignorando watchdog_timeout para otro threadId:', payload?.threadId);
      }
    };
    socketInstance.on('watchdog_timeout', handleWatchdogTimeout);

    // >>> NUEVO Listener para hilos iniciados por N8N
    const handleN8NNewThread = (data) => {
      // data = { thread: newThreadObject, initialItem: newMessageOrQuestionObject }
      console.log('[useWebSocket] Received n8n_new_thread_initiated:', {
        thread: data?.thread ? {
          id: data.thread.id,
          title: data.thread.title ? data.thread.title.substring(0, 20) + '...' : null,
          created_at: data.thread.created_at
        } : null,
        initialItem: data?.initialItem ? '***[initial item data]***' : null
      });
      setNewlyCreatedThreadData(data);
    };
    socketInstance.on('n8n_new_thread_initiated', handleN8NNewThread);
    // <<< FIN NUEVO Listener

    // >>> NUEVO Listener para actualizaciones de título (LLM-generated)
    const handleThreadTitleUpdated = (payload) => {
      console.log('[useWebSocket] 📝 thread_title_updated received:', {
        threadId: payload?.threadId,
        title: payload?.title,
        timestamp: payload?.timestamp
      });

      // Notificar al callback si existe (viene de ThreadContext)
      if (onThreadTitleUpdated && payload?.threadId && payload?.title) {
        onThreadTitleUpdated(payload.threadId, payload.title);
      }
    };
    socketInstance.on('thread_title_updated', handleThreadTitleUpdated);
    // <<< FIN NUEVO Listener

    // Función de limpieza: se ejecuta cuando threadId cambia o el componente se desmonta
    return () => {
      console.log(`useWebSocket: Disconnecting socket for previous threadId: ${threadId}`);
      // Quitar listeners específicos para evitar fugas de memoria
      socketInstance.off('connect', handleConnect);
      socketInstance.off('disconnect', handleDisconnect);
      socketInstance.off('connect_error', handleError);
      socketInstance.off('error', handleError);
      socketInstance.off('agent_typing', handleAgentTyping);
      socketInstance.off('agent_stop_typing', handleAgentStopTyping);
      socketInstance.off('question', handleQuestion);
      socketInstance.off('agents_update', handleAgentsUpdate);
      socketInstance.off('agent_status_updated', handleAgentStatusUpdate); // <-- Quitar NUEVO listener
      socketInstance.off('watchdog_timeout', handleWatchdogTimeout); // ✅ Quitar listener de watchdog
      socketInstance.off('n8n_new_thread_initiated', handleN8NNewThread); // <<< QUITAR NUEVO LISTENER
      socketInstance.off('thread_title_updated', handleThreadTitleUpdated); // <<< QUITAR listener de título
      // Asegúrate de quitar CUALQUIER otro listener que añadas aquí (ej. agent_message)
      socketInstance.disconnect();
      setSocket(null); // Limpiar el estado del socket
      socketRef.current = null; // ✅ Clean up ref
      setIsConnected(false); // Marcar como desconectado
      // ✅ FIX: Limpiar agentStatus al cambiar de thread para evitar que el estado
      // de un thread se muestre en otro thread (bug de fuga de estado)
      setAgentStatus(null);
      // ✅ FIX: Clear waitForConnection timeout on unmount to prevent orphaned timers
      if (waitForConnectionIntervalRef.current) {
        clearTimeout(waitForConnectionIntervalRef.current);
        waitForConnectionIntervalRef.current = null;
      }
    };
  }, [threadId, SOCKET_URL, loadingAuth]); // ✅ FIX: Removed authToken - see useEffect below

  // ✅ FIX: Update token on existing socket WITHOUT reconnecting
  // This prevents full reconnection on TOKEN_REFRESH (~every hour)
  // Socket.IO will use the new token on next automatic reconnection
  useEffect(() => {
    if (!authToken || !socketRef.current) return;

    // Update the auth object on the existing socket
    socketRef.current.auth = { token: authToken };
    console.log(`[useWebSocket] 🔑 Token updated on existing socket (no reconnection triggered)`);
  }, [authToken]);

  // Enviar mensaje - Esta función es obsoleta, el envío es por API REST
  const sendMessage = useCallback((message) => {
     console.warn("useWebSocket sendMessage is obsolete. Use backend API /api/threads/:threadId/messages.");
  }, []); // No dependencies needed

  // answerQuestion method removed - questions feature deprecated

  // Suscribirse a mensajes entrantes - Necesita filtrar por threadId
  // ✅ PERFORMANCE: Uses socketRef instead of socket to prevent recreations
  // This keeps the function stable across renders while accessing current socket
  const subscribeToMessages = useCallback((callback, currentViewedThreadId) => {
    const currentSocket = socketRef.current;
    if (currentSocket) {
      const listener = (messagePayload) => { // messagePayload = { text, timestamp, threadId, color?, agentName? }
        console.log(`Message received on socket ${currentSocket?.id}. Payload threadId: ${messagePayload?.threadId}, Current viewed threadId: ${currentViewedThreadId}`);
        // Filtrar aquí para asegurarse que el mensaje es para el hilo actual
        if (messagePayload?.threadId === currentViewedThreadId) {
           console.log('Message matches current thread. Calling callback.');
           callback(messagePayload);
        } else {
           console.log('Message ignored, threadId mismatch.');
        }
      };

      // ✅ Remover TODOS los listeners previos de 'agent_message' para evitar acumulación
      currentSocket.removeAllListeners('agent_message');
      currentSocket.on('agent_message', listener); // Añadimos el nuevo

      // Devolver una función para desuscribirse CUANDO EL COMPONENTE QUE LLAMA SE DESMONTE
      // o deje de necesitar la suscripción
      return () => {
        console.log(`Unsubscribing from messages for threadId: ${currentViewedThreadId}`);
        currentSocket.off('agent_message', listener);
      };
    }
    // Devolver función no-op si no hay socket
    return () => { console.log("No socket available for message subscription.") };
  }, []); // ✅ EMPTY dependencies - function is stable, uses ref for current value

  // ── Streaming consumer: subscribe to all 5 stream-* events ──
  // Same pattern as subscribeToMessages: stable ref, threadId filter, returns cleanup.
  // Object args (not positional) — see PRP-B D1.
  // Body extracted to `streaming/streamingSubscription.js` (#368 PR-A) as the single
  // source of truth shared with useModularChat. This wrapper only injects the current
  // socket and MUST stay `useCallback([])`: Chat.jsx's streaming effect depends on this
  // identity, so an unstable reference would re-run it every render and CLEAR the live
  // bubble on each token (chat principal flickering to null). Do not inline.
  const subscribeToStreamingEvents = useCallback(
    (handlers, targetThreadId) =>
      subscribeStreamingEvents(socketRef.current, handlers, targetThreadId),
    []
  ); // stable — uses socketRef

  // #274 — Suscripción al estado RAG por-adjunto de chat (`file_rag_status` thread-scoped).
  // ⚠️ CRÍTICO: NO usar removeAllListeners('file_rag_status') — RagJobContext YA escucha
  // ese evento (Knowledge Manager). Se registra/quita SOLO este handler específico.
  // Filtra por threadId (los eventos rol se emiten por emitToUser SIN threadId → se ignoran).
  const subscribeToFileRagStatus = useCallback((callback, currentViewedThreadId) => {
    const currentSocket = socketRef.current;
    if (!currentSocket) return () => {};

    const listener = (payload) => {
      // payload = { threadId, fileId, status, error, warningCount }
      if (payload?.threadId && payload.threadId === currentViewedThreadId) {
        callback(payload);
      }
    };

    currentSocket.on('file_rag_status', listener);
    return () => {
      currentSocket.off('file_rag_status', listener);
    };
  }, []); // stable — uses socketRef

  // <<< NUEVA FUNCION PARA LIMPIAR newlyCreatedThreadData
  const clearNewlyCreatedThreadData = useCallback(() => {
    setNewlyCreatedThreadData(null);
  }, []);

  // ✅ FIX: Clear reconnection flag after messages are refetched
  const clearReconnectedFlag = useCallback(() => {
    setHasReconnected(false);
  }, []);

  // ✅ Ref to track active timeout for cleanup
  const waitForConnectionIntervalRef = useRef(null);

  // ✅ PERF: Promise-based connection waiting (replaces 100ms polling)
  // Uses connectionPromiseRef (set at socket creation, resolved on 'connect' event)
  const waitForConnection = useCallback((timeoutMs = 5000) => {
    // If already connected, resolve immediately
    if (isConnected) {
      console.log('[useWebSocket] Already connected, resolving immediately');
      return Promise.resolve();
    }

    console.log('[useWebSocket] ⏳ Waiting for WebSocket connection...');

    // Use the existing connection promise (created when socket is initialized)
    const connectionPromise = connectionPromiseRef.current || Promise.resolve();

    // Race against a timeout to avoid blocking forever
    const timeoutPromise = new Promise((resolve) => {
      const timerId = setTimeout(() => {
        console.warn(`[useWebSocket] ⏱️ Connection timeout after ${timeoutMs}ms, proceeding anyway`);
        resolve();
      }, timeoutMs);
      // Store for cleanup
      waitForConnectionIntervalRef.current = timerId;
    });

    return Promise.race([connectionPromise, timeoutPromise]).then(() => {
      // Clean up timeout if connection resolved first
      if (waitForConnectionIntervalRef.current) {
        clearTimeout(waitForConnectionIntervalRef.current);
        waitForConnectionIntervalRef.current = null;
      }
    });
  }, [isConnected]);

  // ✅ Función para limpiar el estado de watchdog timeout
  const clearWatchdogTimeout = useCallback(() => {
    setWatchdogTimeout(null);
  }, []);

  return {
    // socket, // No es recomendable exponer el socket directamente
    isConnected,
    isTyping,
    error,
    // sendMessage, // Eliminada o marcada como obsoleta
    subscribeToMessages,
    subscribeToStreamingEvents,
    subscribeToFileRagStatus, // #274 — estado RAG por-adjunto de chat (thread-scoped)
    // currentQuestion, answerQuestion - removed, questions feature deprecated
    agents,
    agentStatus, // <-- Exponer NUEVO estado
    setAgentStatusManually, // <-- Exponer NUEVA función
    newlyCreatedThreadData, // <<< EXPONER NUEVO ESTADO
    clearNewlyCreatedThreadData, // <<< EXPONER NUEVA FUNCION
    watchdogTimeout, // ✅ Estado de watchdog timeout
    clearWatchdogTimeout, // ✅ Función para limpiar watchdog
    waitForConnection, // ✅ CRITICAL: Expose function to wait for connection
    // ✅ FIX: Expose reconnection tracking for message recovery
    hasReconnected,
    clearReconnectedFlag
  };
}; 